import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { validateFloat } from "@/common/validation/Validators";
import { format } from "date-fns";
import _ from "lodash";
import { connect } from "react-redux";
import NoItemsRow from "@/common/NoItemsRow";
import Tooltip from "@/tooltip";
import { PlusIcon } from "@heroicons/react/20/solid";
import { NotificationManager } from "react-notifications";
import AgreementGrade from "../AgreementGrade";
import DownloadForms from "../DownloadForms";
import AllowanceField from "./AllowanceRow";
import { employmentTypeOptions } from "@/components/company-settings/config/employmentTypeOptions";
import BuddySelector from "buddy/selector";
import { Actions as ALLOWANCEACTIONS } from "@/redux/actions/allowanceActions";

import {
	salaryPeriodOption,
	paymentTypeOptions,
	weekdays,
	defaultWorking,
} from "../AgreementOptions";

const todayString = format(new Date(), "yyyy-MM-dd");

const calculateHourlyRate = (workingDays, salaryAmount, weeklyHours, salaryPeriod) => {
	const hours = parseFloat(weeklyHours) || 1;
	let divisable = 1;
	const salary = parseFloat(salaryAmount) || 1;

	switch (String(salaryPeriod)) {
		case "101": //Yearly
			divisable = hours * 52;
			break;
		case "102": //Monthly
			divisable = (hours * 52) / 12;
			break;
		case "103": //Weekly
			divisable = hours;
			break;
		case "106": //Four Weekly
			divisable = hours * 4;
			break;
		case "107": //Two Weekly
			divisable = hours * 2;
			break;
		case "104": //Daily
			divisable = hours / (workingDays || 1);
			break;
		case "105": //Hourly
			divisable = 1;
			break;
	}

	return salary / divisable;
};

const SalaryDetails = ({
	employmentTypeOptions,
	location,
	data={},
	setData,
	allowanceGetList,
	allowanceList,
	overtimeList,
	disabled,
	forNewEmployee,
	gradeApplied,
	employeeName,
	jurisdiction,
	payScheduleOptions,
	isTerminated,
	currency,
	forReview,
	salary,
}) => {
	const [payPerSalary, setPayPerSalary] = useState(false);
	const [payFrequencyDescription, setPayFrequencyDescription] = useState(false);

	const updateField = (field, value) => {
		const newData = {
			...data,
			[field]: value,
		};

		if (["salaryAmount", "salaryPeriod"].includes(field)) {
			let basicPay = false;
			let payDescription = ``;

			const period =
				payScheduleOptions?.length == 1
					? payScheduleOptions[0]
					: payScheduleOptions?.find((item) => item.value == data?.paySchedule);

			switch (String(newData.salaryPeriod)) {
				case "101": //Yearly
					basicPay = newData.salaryAmount / parseInt(period?.payPeriods);
					break;
				case "102": //Monthly
					basicPay = (newData.salaryAmount * 12) / parseInt(period?.payPeriods);
					break;
				case "103": //Weekly
					basicPay = (newData.salaryAmount * 52) / parseInt(period?.payPeriods);
					break;
				case "106": //Four Weekly
					basicPay = (newData.salaryAmount * 13) / parseInt(period?.payPeriods);
					break;
				case "107": //Two Weekly
					basicPay = (newData.salaryAmount * 26) / parseInt(period?.payPeriods);
					break;
				case "104": //Daily
					basicPay = (newData.salaryAmount * 260) / parseInt(period?.payPeriods);
					break;
				case "105": //Hourly
					// na
					break;
			}
			switch (String(period?.payPeriods)) {
				case "13":
					payDescription = "Four Weeks";
					break;
				case "26":
					payDescription = "Fortnight (Two Weeks)";
					break;
				case "52":
					payDescription = "Week";
					break;
				case "12":
					payDescription = "Month";
					break;
			}
			setPayPerSalary(basicPay);
			setPayFrequencyDescription(payDescription);
		}

		setData(newData);
	};
	const updateAllowance = ({ index, type, value }) => {
		const currentValue = data.allowances[index] || {};
		currentValue.type = type ? String(type) : currentValue.type;
		currentValue.amount = value ?? currentValue.amount;

		data.allowances.splice(index, 1, currentValue);
		setData({ ...data });
	};

	const hourlyRate = useMemo(() => {
		return calculateHourlyRate(data.workingDays?.length, data.salaryAmount, data.weeklyHours, data.salaryPeriod);
	}, [data.workingDays?.length, data.salaryAmount, data.weeklyHours, salary?.id]);

	const OvertimeField = ({ index }) => {
		const filteredList = overtimeList.filter((item) => !data.overtime?.includes(item.value));
		const selectedOvertime = overtimeList?.find((item) => item.value == data.overtime[index]);
		return (
			<div className="col-xs-12">
				<div className="col-xs-4 pl-0">
					<div className="form-material form-container">
						<label htmlFor="material-text">Overtime</label>
						<Select
							value={selectedOvertime}
							isDisabled={disabled || gradeApplied}
							onChange={(selected) => {
								data.overtime.splice(index, 1, selected?.value);
								setData({ ...data });
							}}
							options={filteredList}
						/>
					</div>
				</div>
				<div className="col-xs-2 pl-0">
					<div className="form-material form-container">
						<label htmlFor="material-text">Type</label>
						<input
							className="form-control form-input"
							disabled
							value={selectedOvertime ? (selectedOvertime?.isToil ? "Time Off in Lieu" : "Paid") : ""}
						/>
					</div>
				</div>
				<div className="col-xs-2 pl-0">
					<div className="form-material form-container">
						<label>Rate</label>
						<input
							className="form-control form-input"
							disabled
							value={selectedOvertime?.rate}
						/>
					</div>
				</div>
				<div className="col-xs-2 pl-0">
					<div
						className="form-material form-container"
						style={{ position: "relative" }}
					>
						<label>Effective Hour</label>
						<div style={{ position: "absolute", top: 8 }}>{currency}</div>
						<input
							style={{ paddingLeft: "1rem" }}
							className="form-control form-input"
							disabled
							value={selectedOvertime ? (hourlyRate * selectedOvertime?.rate).toFixed(2) : ""}
						/>
					</div>
				</div>
				<div className="col-lg-2 col-xs-2">
					<button
						className="btn pull-right"
						disabled={disabled || gradeApplied}
						style={{ marginTop: "1.25rem" }}
						onClick={() => {
							if (disabled) return;
							setData({
								...data,
								overtime: data.overtime.filter((item) => item != data.overtime[index]),
							});
						}}
					>
						<div style={{ float: "left" }}>Remove</div>
					</button>
				</div>
			</div>
		);
	};

	useEffect(() => {
		allowanceGetList();
	}, []);

	return (
		<>
			<div className="col-xs-12 form-section">
				<div className="col-xs-12 form-container">
					<div className="pull-left mt-8">
						<h1 className="block-title">Salary Details</h1>
					</div>
					<div className="pull-right">
						{!forReview && !forNewEmployee && location == "Employee" ? (
							<DownloadForms
								{...{ agreementId: salary?.agreement, salarySettingId: salary?.id, formType: "salary" }}
							/>
						) : null}
					</div>
				</div>

				<div className="col-xs-12 form-container">
					{location !== "gradeModal" && payScheduleOptions?.length > 1 && (
						<div
							className="col-lg-3 col-xs-3"
							style={{ paddingLeft: 0 }}
						>
							<div
								className="form-material"
								style={{ paddingLeft: 0 }}
							>
								<span className="salary-error span-alert"></span>
								<label htmlFor="material-text">Pay Schedule*</label>
								<Select
									name="paySchedule"
									options={payScheduleOptions}
									value={payScheduleOptions?.find((item) => item.value == data.paySchedule)}
									isDisabled={disabled || isTerminated}
									onChange={(selected) => {
										updateField("paySchedule", selected?.value);
									}}
								/>
							</div>
						</div>
					)}
					{!forReview && location == "Employee" ? (
						<AgreementGrade
							forNewEmployee={forNewEmployee}
							salary={salary}
							disabled={disabled || gradeApplied}
							hideButton={isTerminated}
						/>
					) : null}
				</div>
				<div
					className="col-xs-12"
					style={{ paddingBottom: 20 }}
				>
					<div className="col-lg-3 col-xs-3 pl-0">
						<div
							className="form-material"
							style={{ position: "relative" }}
						>
							<label>Salary*</label>
							<div style={{ position: "absolute", top: 8 }}>{currency}</div>
							<input
								style={{ paddingLeft: "1rem" }}
								className="form-control form-input"
								id="salaryAmount"
								name="salaryAmount"
								value={data.salaryAmount}
								onChange={(e) => {
									updateField(e.target.name, validateFloat(e.target.value));
								}}
								disabled={disabled || gradeApplied}
								autoComplete="off"
							/>
						</div>
					</div>

					<div className="col-lg-3 col-xs-3 pl-0">
						<div className="form-material">
							<label htmlFor="salaryPeriod">Every*</label>
							<Select
								options={salaryPeriodOption}
								value={salaryPeriodOption?.find((item) => item.value == data.salaryPeriod)}
								placeholder="Select Period"
								isDisabled={disabled || gradeApplied}
								onChange={(selected) => {
									updateField("salaryPeriod", selected.value);
								}}
							/>
						</div>
					</div>

					<div className="col-lg-3 col-xs-3 pl-0">
						<div className="form-material">
							<label htmlFor="net">Salary Type</label>
							<BuddySelector
								options={paymentTypeOptions}
								value={paymentTypeOptions.find((item) => item.value == data.net)}
								placeholder="Choose one"
								isDisabled={disabled || gradeApplied}
								onChange={(selected) => updateField("net", selected.value)}
							/>
						</div>
					</div>

					<div className="col-lg-3 col-xs-3 pl-0">
						<div className="form-material">
							<label htmlFor="material-text">Employment Type*</label>
							<Select
								options={employmentTypeOptions.filter(
									(item) => !item.jurisdiction || item.jurisdiction.includes(jurisdiction),
								)}
								value={employmentTypeOptions.find((item) => item.value == data.employmentType)}
								placeholder="Choose one"
								isDisabled={disabled || gradeApplied}
								onChange={(selected) => {
									updateField("employmentType", selected.value);
								}}
							/>
						</div>
					</div>

					{payPerSalary && (
						<div className="col-xs-12 pl-0">
							{employeeName || "Employee"} will be paid{" "}
							<strong>
								{currency}
								{payPerSalary.toLocaleString(undefined, {
									minimumFractionDigits: 0,
									maximumFractionDigits: 2,
								})}
							</strong>{" "}
							every {payFrequencyDescription}
						</div>
					)}
				</div>

				<div className="col-xs-12 form-container pl-0">
					<div className="col-lg-3 col-xs-3">
						<div className="form-material">
							<label
								className="css-input switch switch-sm switch-success"
								style={{ cursor: disabled ? "not-allowed" : "pointer" }}
							>
								<input
									id="casual"
									name="casual"
									checked={data.casual}
									onClick={(e) => updateField(e.target.name, !data[e.target.name])}
									onChange={(e) => {}} //To Prevent Error Spamming in Console
									type="checkbox"
									disabled={disabled || gradeApplied}
								/>
								<span></span>
								<label
									htmlFor="material-text"
									style={{ marginBottom: 0 }}
								>
									Variable Hours
								</label>
							</label>
						</div>
					</div>

					{jurisdiction == "MT" && (
						<>
							<div className="col-lg-3 col-xs-3">
								<div className="form-material">
									<label
										className="css-input switch switch-sm switch-success"
										style={{
											cursor: disabled ? "not-allowed" : "pointer",
										}}
									>
										<input
											id="suspendBonus"
											name="suspendBonus"
											checked={data.suspendBonus}
											onClick={(e) => updateField(e.target.name, !data[e.target.name])}
											onChange={() => {}} //To Prevent Error Spamming in Console
											type="checkbox"
											disabled={disabled || gradeApplied}
										/>
										<span></span>
										<label
											htmlFor="material-text"
											style={{ marginBottom: 0 }}
										>
											<Tooltip content="Only tick this checkbox if the contracts clearly stipulate that the salary provided is inclusive of Government Bonuses, otherwise the Government bonuses are given over and above normal pay rates.">
												Suspend Bonus
											</Tooltip>
										</label>
									</label>
									<div className="clearfix"></div>
								</div>
							</div>

							<div className="col-lg-3 col-xs-3">
								{overtimeList?.length > 0 && (
									<div className="form-material">
										<label
											className="css-input switch switch-sm switch-success"
											style={{
												cursor: disabled ? "not-allowed" : "pointer",
											}}
										>
											<input
												id="isManagerial"
												name="isManagerial"
												checked={data.isManagerial}
												onClick={(e) => updateField(e.target.name, !data[e.target.name])}
												onChange={() => {}} //To Prevent Error Spamming in Console
												type="checkbox"
												disabled={disabled || gradeApplied}
											/>
											<span></span>
											<label
												htmlFor="material-text"
												style={{ marginBottom: 0 }}
											>
												<Tooltip content="Only tick this checkbox if the employee has a managerial role, and shall not benefit from the 15% Overtime rate, should their earnings be under €20,000/yr.">
													OT Tax Opt Out
												</Tooltip>
											</label>
										</label>
										<div className="clearfix"></div>
									</div>
								)}
							</div>
						</>
					)}
				</div>

				<div className="clearfix"></div>

				{!data.casual && (
					<>
						<div className="col-lg-6 col-xs-6">
							<div className="form-material">
								<label htmlFor="material-text">Working days</label>
								<div className="push-20-t">
									{weekdays?.map((weekday, index) => (
										<div style={{ float: "left" }}>
											<label
												className="css-input css-checkbox css-checkbox-primary remove-margin-t remove-margin-b"
												style={{
													cursor: disabled ? "not-allowed" : "pointer",
												}}
											>
												<input
													className="working-days-check"
													type="checkbox"
													name={`workingDay${index + 1}]`}
													checked={data.workingDays?.includes(String(index + 1)) || false}
													onClick={() => {
														let workingDays = data.workingDays;
														if (workingDays && workingDays.includes(String(index + 1)))
															workingDays = workingDays?.filter(
																(item) => item != String(index + 1),
															);
														else
															workingDays = _.sortedUniq([
																...(workingDays || []),
																String(index + 1),
															]);

														updateField("workingDays", workingDays);
													}}
													disabled={disabled || gradeApplied}
												/>
												<span></span>
												{weekday} &nbsp;
											</label>
										</div>
									))}
								</div>
							</div>
						</div>
						<div className="col-lg-3 col-xs-3">
							<div className="form-material">
								<label htmlFor="material-text">Weekly Hours</label>
								<input
									className="form-control form-input"
									type="text"
									name="weeklyHours"
									value={data.weeklyHours || ""}
									onChange={(e) => {
										updateField(e.target.name, validateFloat(e.target.value));
									}}
									disabled={disabled || gradeApplied}
									autoComplete="off"
								/>
							</div>
						</div>
					</>
				)}
				{data.casual && jurisdiction == "MT" && (
					<div className="col-lg-3 col-xs-3">
						<div className="form-material">
							<label htmlFor="previousHours">
								<Tooltip content="This field is only required when starting out on Buddy, and an employee has been working from the year before with variable hours. This ensures correct calculation of Government Bonus for March. Insert the number of hours worked from October to December. If your employee is on fixed hours, this will be automatically calculated, and is not required.">
									Hours worked in Q4
								</Tooltip>
							</label>
							<input
								className="form-control form-input"
								id="previousHours"
								name="previousHours"
								disabled={disabled || gradeApplied}
								value={data.previousHours || ""}
								onChange={(e) => {
									updateField(e.target.name, validateFloat(e.target.value));
								}}
							/>
						</div>
					</div>
				)}
				<div
					className="clearfix"
					style={{ paddingBottom: "2rem" }}
				/>
			</div>
			<div className="col-xs-12 form-section">
				<div className="col-xs-12 form-container">
					<div className="pull-left">
						<h1 className="block-title">Overtime & Special Rates</h1>
					</div>
					<div className="pull-right">
						<Tooltip
							content={!overtimeList?.length &&
							"Create Overtimes in Payroll Settings"}
							isOpen={overtimeList?.length && false}
						>
								<button
									className="btn btn-success pull-right"
									type="button"
								disabled={disabled || !overtimeList?.length || gradeApplied}
									onClick={() => {
									if (disabled)
										return NotificationManager.error(
											"You do not have permission to modify Overtimes",
											"Overtime & Special Rates",
										);
										setData({
											...data,
										overtime: data.overtime ? [...data.overtime, {}] : [{}],
										});
									}}
								>
									<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
									<div style={{ float: "left" }}>Add Rate</div>
								</button>
						</Tooltip>
					</div>
				</div>
				{data.overtime?.length > 0 ? (
					data.overtime?.map((allowance, index) => <OvertimeField index={index} />)
				) : (
					<table className="js-table-sections table table-hover">
						<NoItemsRow name="Overtime or Special Rates" />
					</table>
				)}
			</div>

			<div className="col-xs-12 form-section">
				<div className="col-xs-12 form-container">
					<div className="pull-left">
						<h1 className="block-title">{jurisdiction == "UK" ? "Pay Items" : "Allowances"}</h1>
					</div>
					<div className="pull-right">
						{allowanceList?.length != data.allowances?.length && !disabled && !isTerminated && (
							<button
								className="btn btn-success pull-right"
								type="button"
								onClick={() => {
									const allowances = data.allowances || [];
									allowances.push({ outsideOfGrade: true });
									updateField("allowances", allowances);
									if (isTerminated || !gradeApplied) return;
									NotificationManager.info(
										`This ${
											jurisdiction == "UK" ? "Pay Item" : "Allowance"
										} will be added outside of grade, and will not affect existing grades`,
										`Adding ${jurisdiction == "UK" ? "Pay Items" : "Allowances"}`,
									);
								}}
							>
								<PlusIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
								<div style={{ float: "left" }}>
									Add {jurisdiction == "UK" ? "Pay Item" : "Allowance"}
								</div>
							</button>
						)}
					</div>

					<p
						className="col-xs-12 help-block"
						style={{ padding: 0, marginBottom: 0 }}
					>
						Recurring {jurisdiction == "UK" ? "pay item" : "allowance"} values are on a per payroll basis.
						Insert the amounts you want to give your employee on every payslip. <br />
						One-off {jurisdiction == "UK" ? "pay items" : "allowances"} should be added from within the
						Payroll itself.
					</p>
				</div>
				{allowanceList.length && data.allowances?.length > 0 ? (
					data.allowances?.map((allowance, index) => (
						<AllowanceField
							index={index}
							filteredList={allowanceList.filter(
								(item) =>
									!data.allowances?.map((allowance) => allowance?.type).includes(String(item.id)),
							)}
							allowances={data.allowances}
							selectedAllowance={allowanceList?.find(
								(item) => item.value == data.allowances?.[index]?.type,
							)}
							allowanceList={allowanceList}
							disabled={(gradeApplied && !allowance.outsideOfGrade) || isTerminated || disabled}
							updateAllowance={({ type, value }) => {
								updateAllowance({ index, type, value });
							}}
							setAllowances={(allowances) => updateField("allowances", allowances)}
						/>
					))
				) : (
					<table className="js-table-sections table table-hover">
						<NoItemsRow name={jurisdiction == "UK" ? "Pay Items" : "Allowances"} />
					</table>
				)}
			</div>
			<div className="clearfix"></div>
		</>
	);
};

const mapStateToProps = (state, ownProps) => {
	const isCis = state.employee?.agreementInfo?.type === "cis";
	return {
		employmentTypeOptions: employmentTypeOptions.filter(t => isCis ? t.value === "cis-contractor" : t.value !== "cis-contractor"),
		jurisdiction: state.company?.settings?.jurisdiction,
		allowanceList: [
			...(state.allowances?.allowanceList?.filter(
				(a) => !a.public || a.public == "no" || a.canBeRecurring == "yes" || a.canBeRecurring == true,
			) || []),
			...(state?.company?.settings?.allowances?.filter((allowance) => !allowance.temporary) || []),
		].map((allowance) => ({
			...allowance,
			label: allowance.allowanceName || allowance.name,
			value: allowance.id,
		})),
		overtimeList: state?.company?.settings?.overtimes
			?.filter((overtime) => !overtime.temporary)
			?.map((item) => ({
				...item,
				label: item.name,
				value: item.id,
			})),
		currency: state.company?.settings?.currency,
		payScheduleOptions: state.company?.settings?.paySchedules
			?.filter((ps) => !ps.temporary && (isCis ? ps.type === "cis" : ps.type !== "cis"))
			?.map((ps) => ({
				label: ps.name,
				value: ps.id,
				payPeriods: ps.payFrequency,
			})),
	};
};

const mapDispatchToProps = (dispatch) => ({
	allowanceGetList: () => dispatch(ALLOWANCEACTIONS.allowanceGetList()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SalaryDetails);
